import VSelect from 'vue3-select-component'
import 'vue3-select-component/styles'
import { defineComponent, h, ref } from 'vue'

export default defineComponent({
    name: 'VSelectWrapper',
    props: {
        label: { type: String, default: null },
        reduce: { type: Function, default: null }
    },
    inheritAttrs: false,
    emits: ['update:modelValue'],
    setup(props, { attrs, slots, emit }) {
        // Current search text, tracked so Enter can pick the first filtered option.
        const search = ref('')

        return () => {
            // shallow copy attrs to normalize
            const baseAttrs = { ...attrs }

            // normalize `disabled` (boolean attribute / empty string) -> isDisabled
            if (baseAttrs.disabled !== undefined && baseAttrs.isDisabled === undefined) {
                baseAttrs.isDisabled = baseAttrs.disabled === '' ? true : baseAttrs.disabled
                delete baseAttrs.disabled
            }

            // normalize kebab-case `is-disabled`
            if (baseAttrs['is-disabled'] !== undefined && baseAttrs.isDisabled === undefined) {
                baseAttrs.isDisabled = baseAttrs['is-disabled'] === '' ? true : baseAttrs['is-disabled']
                delete baseAttrs['is-disabled']
            }

            // resolve getOptionLabel / getOptionValue
            const explicitGetOptionLabel = baseAttrs.getOptionLabel || baseAttrs['get-option-label']
            const explicitGetOptionValue = baseAttrs.getOptionValue || baseAttrs['get-option-value']

            const getOptionLabel = explicitGetOptionLabel ||
                (props.label ? (opt => (opt == null ? undefined : opt[props.label])) : (opt => (opt == null ? undefined : opt.label)))

            const getOptionValue = explicitGetOptionValue || (props.reduce ? props.reduce : (opt => (opt == null ? undefined : opt.value)))

            // same default matcher vue3-select-component uses
            const filterBy = baseAttrs.filterBy || baseAttrs['filter-by'] ||
                ((option, label, term) => String(label ?? '').toLowerCase().includes(String(term ?? '').toLowerCase()))

            const isMulti = baseAttrs.isMulti === true || baseAttrs.isMulti === '' || baseAttrs['is-multi'] === true

            // ensure update:modelValue is forwarded to wrapper emit if not provided
            const onUpdateModel = baseAttrs['onUpdate:modelValue'] || ((v) => emit('update:modelValue', v))

            // track the search text (and still forward any user-supplied @search handler)
            const userOnSearch = baseAttrs.onSearch
            const onSearch = (value) => {
                search.value = value ?? ''
                if (typeof userOnSearch === 'function') userOnSearch(value)
            }

            // Enter-to-select fix. The library resets the focused option to -1 on every search change, so
            // "type to filter" leaves nothing focused and its Enter selects nothing. If the user already
            // arrowed to an option we let the library handle Enter; otherwise we select the first filtered
            // option ourselves and close the menu with Escape (closeMenu without clearing the search).
            // NOTE: this relies on the accompanying patch to vue3-select-component that stops the menu from
            // re-opening when the search clears after a selection (patches/vue3-select-component+0.14.1).
            const onKeydownCapture = (event) => {
                if (event.key !== 'Enter' || isMulti) return
                const term = (search.value || '').trim()
                if (!term) return
                if (document.querySelector('.menu-option.focused')) return

                const options = Array.isArray(attrs.options) ? attrs.options : []
                const match = options.find(opt => !opt?.disabled && filterBy(opt, getOptionLabel(opt), term))
                if (!match) return

                event.preventDefault()
                event.stopPropagation()
                onUpdateModel(getOptionValue(match))
                search.value = ''

                // Close after the value update settles. Escape -> closeMenu() on the LIVE input (the update
                // replaces the original element, so the captured target is stale).
                setTimeout(() => {
                    const live = document.activeElement
                    if (live && live.tagName === 'INPUT' && typeof live.dispatchEvent === 'function') {
                        live.dispatchEvent(new KeyboardEvent('keydown', {
                            key: 'Escape', code: 'Escape', bubbles: true, cancelable: true
                        }))
                    }
                }, 0)
            }

            const vnodeProps = {
                ...baseAttrs,
                ...(getOptionLabel ? { getOptionLabel } : {}),
                ...(getOptionValue ? { getOptionValue } : {}),
                onSearch,
                'onUpdate:modelValue': onUpdateModel
            }

            // display:contents keeps layout identical (no extra box) while still receiving the captured keydown.
            return h('div', { class: 'v-select-wrapper', style: 'display: contents', onKeydownCapture }, [
                h(VSelect, vnodeProps, slots)
            ])
        }
    }
})
